import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { Pipeline } from "../../types";
import { getBottomPosition } from "../positions";
import { useRefreshBoard } from "../useRefreshBoard";
import { DEFAULT_STAGE_KEYS } from "./defaultStages";

export interface PipelineDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pipeline to rename; when absent the dialog creates a pipeline. */
  pipeline?: Pipeline | null;
  pipelines: Pipeline[];
  onSaved: (pipelineId: Identifier) => void;
}

export const PipelineDialog = ({
  open,
  onClose,
  pipeline,
  pipelines,
  onSaved,
}: PipelineDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refreshBoard = useRefreshBoard();
  const [name, setName] = useState("");
  const [withDefaultStages, setWithDefaultStages] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(
        pipeline?.name ??
          (pipelines.length === 0
            ? translate("crm.operations.pipelines.default_name")
            : ""),
      );
      setWithDefaultStages(true);
    }
  }, [open, pipeline, pipelines.length, translate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setIsSaving(true);
    try {
      if (pipeline) {
        await dataProvider.update("pipelines", {
          id: pipeline.id,
          data: { name: name.trim() },
          previousData: pipeline,
        });
        notify("crm.operations.pipelines.updated");
        onSaved(pipeline.id);
      } else {
        const { data: created } = await dataProvider.create<Pipeline>(
          "pipelines",
          {
            data: {
              name: name.trim(),
              position: getBottomPosition(pipelines),
            },
          },
        );
        const stageNames = withDefaultStages
          ? DEFAULT_STAGE_KEYS.map((key) =>
              translate(`crm.operations.pipelines.default_stages.${key}`),
            )
          : [translate("crm.operations.pipelines.default_stages.new_lead")];
        // Sequential: positions must follow the order of the list
        for (const [index, stageName] of stageNames.entries()) {
          await dataProvider.create("pipeline_stages", {
            data: {
              pipeline_id: created.id,
              name: stageName,
              position: index + 1,
            },
          });
        }
        notify("crm.operations.pipelines.created");
        onSaved(created.id);
      }
      onClose();
      await refreshBoard();
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "ra.notification.http_error",
        { type: "error" },
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {translate(
                pipeline
                  ? "crm.operations.pipelines.rename"
                  : "crm.operations.pipelines.create",
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="pipeline-name">
              {translate("crm.operations.pipelines.name")}
            </Label>
            <Input
              id="pipeline-name"
              autoFocus
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          {!pipeline && (
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={withDefaultStages}
                onCheckedChange={(checked) => setWithDefaultStages(!!checked)}
              />
              {translate("crm.operations.pipelines.with_default_stages")}
            </Label>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {translate("ra.action.cancel")}
            </Button>
            <Button type="submit" disabled={isSaving || !name.trim()}>
              {translate("ra.action.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
